import React from 'react';

import _snakeCase from 'lodash/snakeCase';

import { getGoogleLocationProviderSetting } from 'components/modules/business-profile/services/provider-service';
import { getLocationDisplay } from 'components/modules/location/services';
import { multiCaseGet } from 'libs/format';
import { capitalizeAll } from 'libs/strings';

import {
  getBusinessType,
  getLocationAddressDisplay,
} from '../AccountLocationList/AccountLocationList.utils';

function detailRow(label, value) {
  if (value == null || value === '') {
    return null;
  }
  return { label, value: String(value) };
}

export function getInternalLocationLabel(fishermanLocation, internalLocationId) {
  if (fishermanLocation) {
    const name =
      multiCaseGet(fishermanLocation, 'locationName') || getLocationDisplay(fishermanLocation);
    const city = multiCaseGet(fishermanLocation, 'city');
    return [name, city].filter((part) => !!part).join(', ');
  }
  if (internalLocationId != null) {
    return `Location ${internalLocationId}`;
  }
  return 'Unknown';
}

export function getGoogleLocationLabel(gmbLocation) {
  const title = multiCaseGet(gmbLocation, 'title');
  const locality = multiCaseGet(gmbLocation, 'storefrontAddress.locality');
  return [title, locality].filter((part) => !!part).join(', ');
}

export function getInternalLocationDetails(fishermanLocation, internalLocationId) {
  if (!fishermanLocation) {
    return [detailRow('Location ID', internalLocationId ?? 'Unknown')].filter(Boolean);
  }

  return [
    detailRow(
      'Name',
      multiCaseGet(fishermanLocation, 'locationName') || getLocationDisplay(fishermanLocation),
    ),
    detailRow('Street', multiCaseGet(fishermanLocation, 'street')),
    detailRow('City', multiCaseGet(fishermanLocation, 'city')),
    detailRow('State', multiCaseGet(fishermanLocation, 'state')),
    detailRow(
      'ZIP',
      multiCaseGet(fishermanLocation, 'zipCode') || multiCaseGet(fishermanLocation, 'zip_code'),
    ),
    detailRow('Location ID', fishermanLocation.id),
  ].filter(Boolean);
}

export function getGoogleLocationDetails(gmbLocation) {
  const metadata = multiCaseGet(gmbLocation, 'metadata') || {};
  const duplicateLocation = multiCaseGet(metadata, 'duplicateLocation');
  const hasVoiceOfMerchant = multiCaseGet(metadata, 'hasVoiceOfMerchant');
  const storefrontAddress = multiCaseGet(gmbLocation, 'storefrontAddress') || {};
  const postalCode = multiCaseGet(storefrontAddress, 'postalCode');
  const administrativeArea = multiCaseGet(storefrontAddress, 'administrativeArea');

  return [
    detailRow('Title', multiCaseGet(gmbLocation, 'title')),
    detailRow('Address', getLocationAddressDisplay(gmbLocation)),
    detailRow('Locality', multiCaseGet(storefrontAddress, 'locality')),
    detailRow('State', administrativeArea),
    detailRow('Postal code', postalCode),
    detailRow('Google location ID', multiCaseGet(gmbLocation, 'locationId')),
    detailRow('Place ID', multiCaseGet(metadata, 'placeId')),
    detailRow('Maps URI', multiCaseGet(metadata, 'mapsUri')),
    detailRow('Business type', getBusinessType(metadata)),
    detailRow('Verified by merchant', hasVoiceOfMerchant ? 'Yes' : 'No'),
    detailRow(
      'Duplicate location',
      duplicateLocation ? 'Possible duplicate — verify before changing links' : 'No',
    ),
  ].filter(Boolean);
}

export function getLocationInfoModalDetails(modal) {
  if (!modal) {
    return [];
  }
  if (modal.type === 'internal') {
    return getInternalLocationDetails(modal.fishermanLocation, modal.internalLocationId);
  }
  if (modal.type === 'google') {
    return getGoogleLocationDetails(modal.gmbLocation);
  }
  return [];
}

export function getLocationSyncSettings(locationSettings = [], location) {
  const setting = getGoogleLocationProviderSetting(locationSettings, location);
  return multiCaseGet(setting, 'serviceData.settings') || {};
}

export function getDisplaySyncSetting(settingName, brandName) {
  const CUSTOM_DISPLAY_BY_NAME = {
    hours: 'Operating Hours',
    menu: 'Food Menu',
    website: `${brandName} Website URL`,
    title: 'Location Name',
    profile: 'Profile Description',
    services: 'Services',
  };

  let displayName = CUSTOM_DISPLAY_BY_NAME[settingName];

  if (displayName) {
    return displayName;
  }

  displayName = _snakeCase(settingName);
  displayName = displayName.replace('_', ' ');
  displayName = capitalizeAll(displayName);

  return displayName;
}

export function getPushHelpTooltip(settingName, brandName, menuLabel) {
  const CUSTOM_PUSH_HELP_TOOLTIP = {
    hours:
      'When enabled we will push updates to your Operating Hours to Google when you publish your site.',
    services: (
      <>
        <p>
          <strong>All Items:</strong> Use this to push your entire catalog to Google, regardless of
          whether it&apos;s tied to this location or any location at all.
        </p>
        <p>
          <strong>Only Associated Items:</strong> Best for locations with tightly controlled
          offerings.
        </p>
        <p>
          <strong>Associated + Unassigned Items:</strong> Ideal if you have general
          services/products not tied to a location but still want them listed.
        </p>
      </>
    ),
  };

  const customPushHelpTooltip = CUSTOM_PUSH_HELP_TOOLTIP[settingName];

  if (customPushHelpTooltip) {
    return customPushHelpTooltip;
  }

  const displayName = getDisplaySyncSetting(settingName, brandName, menuLabel);
  return `When enabled we will automatically push your ${displayName} to Google when you publish your site.`;
}

export function getPullHelpTooltip(settingName, brandName, menuLabel) {
  const CUSTOM_PULL_HELP_TOOLTIP = {};

  const customPullHelpTooltip = CUSTOM_PULL_HELP_TOOLTIP[settingName];

  if (customPullHelpTooltip) {
    return customPullHelpTooltip;
  }

  const displayName = getDisplaySyncSetting(settingName, brandName, menuLabel);
  return `When enabled we will automatically pull your ${displayName} from Google.`;
}
